import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildMessagesReportFetchKey,
  formatMessagesReportBoundaryDate,
} from "../../lib/utils/messagesReport"
import { normalizePulseApiPayload } from "../../lib/utils/pulseApiRows"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

/**
 * GET /api/v1/report/messages/clients/{clientId}/sentiments?startDate=&endDate=
 */
export const fetchClientSentimentsReport = createAsyncThunk(
  "clientSentimentsReport/fetch",
  async ({ clientId, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const start = formatMessagesReportBoundaryDate(startDate)
      const end = formatMessagesReportBoundaryDate(endDate)
      const query = new URLSearchParams({ startDate: start, endDate: end }).toString()
      const data = await get(
        `report/messages/clients/${clientId}/sentiments?${query}`,
      )

      return {
        rows: normalizePulseApiPayload(data),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildMessagesReportFetchKey(clientId, start, end),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch sentiments report")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { clientId, startDate, endDate } = arg || {}
      const start = formatMessagesReportBoundaryDate(startDate)
      const end = formatMessagesReportBoundaryDate(endDate)
      const key = buildMessagesReportFetchKey(clientId, start, end)
      return !skipIfFetchKeyLoaded(getState().clientSentimentsReport, key)
    },
  },
)

const initialState = {
  rows: [],
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginClientSentimentsReportFetch(state, action) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatMessagesReportBoundaryDate(startDate)
  const end = formatMessagesReportBoundaryDate(endDate)
  state.status = "loading"
  state.error = null
  state.requestedFetchKey = buildMessagesReportFetchKey(clientId, start, end)
  state.fetchedFetchKey = null
  state.rows = []
}

function isStaleClientSentimentsReportFetch(action, state) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatMessagesReportBoundaryDate(startDate)
  const end = formatMessagesReportBoundaryDate(endDate)
  const key = buildMessagesReportFetchKey(clientId, start, end)
  return key !== String(state.requestedFetchKey ?? "")
}

const clientSentimentsReportSlice = createSlice({
  name: "clientSentimentsReport",
  initialState,
  reducers: {
    clearClientSentimentsReport: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientSentimentsReport.pending, (state, action) => {
        beginClientSentimentsReportFetch(state, action)
      })
      .addCase(fetchClientSentimentsReport.fulfilled, (state, action) => {
        if (isStaleClientSentimentsReportFetch(action, state)) return
        state.status = "succeeded"
        state.rows = action.payload?.rows ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientSentimentsReport.rejected, (state, action) => {
        if (isStaleClientSentimentsReportFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch sentiments report"
        state.rows = []
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientSentimentsReport } = clientSentimentsReportSlice.actions
export default clientSentimentsReportSlice.reducer
